import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Address,
  AddressUtil,
  BatchTrade__factory,
  IComptrollerLib,
  IComptrollerLib__factory,
  IGasRelayPaymasterLib,
  IGasRelayPaymasterLib__factory,
  IVault__factory,
} from '@31third/common';
import { APP_CONFIG } from 'app-config';
import {
  AlertService,
  BLOCK_EXPLORER_TX_LINK,
  ChainRepository,
  SignerService,
  Trade,
  TradeSateEnum,
  Transaction,
  TransactionType,
} from 'common';
import {
  catchError,
  lastValueFrom,
  Observable,
  retry,
  throwError,
  timer,
} from 'rxjs';
import { Contract, Interface, JsonRpcSigner } from 'ethers';
import {
  RebalancingRepository,
  TransactionExecutionRepository,
} from '../repository';
import { TransactionService } from './transaction.service';
import { Rebalancing } from '../model';
import { RebalancingStep } from '../component';
import { SentryService } from './sentry.service';
import { TransactionExecutionStep } from '../enum';

interface RelayRequestSignature {
  from: Address;
  to: Address;
  value: bigint;
  gas: bigint;
  nonce: bigint;
  data: string;
  validUntil: bigint;
  relayData: {
    gasPrice: bigint;
    pctRelayFee: bigint;
    baseRelayFee: bigint;
    relayWorker: Address;
    paymaster: Address;
    forwarder: Address;
    paymasterData: string;
    clientId: bigint;
  };
}

const signatureTypes = {
  RelayRequest: [
    { name: 'from', type: 'address' },
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'gas', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'data', type: 'bytes' },
    { name: 'validUntil', type: 'uint256' },
    { name: 'relayData', type: 'RelayData' },
  ],
  RelayData: [
    { name: 'gasPrice', type: 'uint256' },
    { name: 'pctRelayFee', type: 'uint256' },
    { name: 'baseRelayFee', type: 'uint256' },
    { name: 'relayWorker', type: 'address' },
    { name: 'paymaster', type: 'address' },
    { name: 'forwarder', type: 'address' },
    { name: 'paymasterData', type: 'bytes' },
    { name: 'clientId', type: 'uint256' },
  ],
};

interface RelayServerRequest {
  metadata: {
    approvalData: string;
    relayHubAddress: Address;
    relayMaxNonce: number;
    signature: string;
  };
  relayRequest: {
    request: {
      from: Address;
      to: Address;
      value: bigint;
      gas: bigint;
      nonce: bigint;
      data: string;
      validUntil: bigint;
    };
    relayData: {
      gasPrice: bigint;
      pctRelayFee: bigint;
      baseRelayFee: bigint;
      relayWorker: Address;
      paymaster: Address;
      forwarder: Address;
      paymasterData: string;
      clientId: bigint;
    };
  };
}

interface RelayServerResponse {
  signedTx?: string;
  error?: string;
}

interface ChainConfig {
  relayerUrl: string;
  pctRelayFee: bigint;
  chainIdAsDecimal: number;
}

interface RelayerGetaddr {
  relayWorkerAddress: Address;
  relayManagerAddress: Address;
  relayHubAddress: Address;
  ownerAddress: Address;
  minGasPrice: string;
  maxAcceptanceBudget: string;
  chainId: string;
  networkId: string;
  ready: boolean;
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class EnzymeGasRelayerService {
  private chainConfigs: { [chainId: string]: ChainConfig } = {
    '0x1': {
      relayerUrl: 'https://relay.enzyme.finance',
      pctRelayFee: 10n,
      chainIdAsDecimal: 1,
    },
    '0x89': {
      relayerUrl: 'https://relay-polygon.enzyme.finance',
      pctRelayFee: 70n,
      chainIdAsDecimal: 137,
    },
  };

  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private readonly httpClient: HttpClient,
    private readonly signerService: SignerService,
    private readonly alertService: AlertService,
    private readonly errorHandler: ErrorHandler,
    private readonly chainRepository: ChainRepository,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly transactionService: TransactionService,
    private readonly sentryService: SentryService,
    private readonly transactionExecutionRepository: TransactionExecutionRepository,
  ) {}

  public async executeViaReleayer(): Promise<void> {
    const signer = this.signerService.getSigner();
    if (!signer) {
      throw new Error('Wallet not connected anymore');
    }

    const rebalancing = this.rebalancingRepository.store.getValue().rebalancing;
    if (!rebalancing) {
      throw new Error('No rebalancing calculated');
    }

    this.transactionExecutionRepository.startExecution(true);
    this.sentryService.captureMsg('executeViaReleayer() called');

    this.rebalancingRepository.setStep(RebalancingStep.IN_PROGRESS);
    rebalancing.trades.forEach(trade => {
      this.rebalancingRepository.updateTrade(trade.id, {
        tradeState: TradeSateEnum.RUNNING,
      });
    });

    try {
      const gasEstimate = await this.estimateGasUnits(rebalancing); // TODO: optimize error handling

      let config;
      let getaddr;
      let relayRequest;
      try {
        this.transactionExecutionRepository.setStep(
          TransactionExecutionStep.PREPARE_RELAYER_REQUEST,
        );
        config = this.getConfig();
        getaddr = await this.getAddr(config);
        relayRequest = await this.prepareRelayRequest(
          rebalancing,
          signer,
          gasEstimate,
          config,
          getaddr,
        );
      } catch (e) {
        this.transactionExecutionRepository.setFailedStep(
          TransactionExecutionStep.PREPARE_RELAYER_REQUEST,
        );
        throw e;
      }

      const signature = await this.askForSignature(
        relayRequest,
        signer,
        config,
      );

      // TODO: maybe check if gasPrice (or other things changed too much)

      this.alertService.showInfo('rebalancing.getSignedTxFromRelayer');

      const signedTx = await this.getSignedTransactionFromRelayer(
        relayRequest,
        signature,
        config,
        getaddr,
      );

      await this.broadcastTransaction(rebalancing, signedTx, signer);
    } catch (err) {
      this.sentryService.captureError(err as Error);

      this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
      rebalancing.trades.forEach(trade => {
        this.rebalancingRepository.updateTrade(trade.id, {
          tradeState: TradeSateEnum.READY,
        });
      });

      this.alertService.showError('rebalancing.error.couldntBeExecuted');

      throw err;
    }
  }

  private async prepareRelayRequest(
    rebalancing: Rebalancing,
    signer: JsonRpcSigner,
    gasEstimate: bigint,
    config: ChainConfig,
    getaddr: RelayerGetaddr,
    retries = 2,
    initialDelay = 100,
  ): Promise<RelayRequestSignature> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.PREPARE_RELAYER_REQUEST,
    );

    let retryCount = 0;
    while (retryCount <= retries) {
      try {
        const paymaster = await this.getGasRelayPaymaster(
          rebalancing.txHandler!,
        );
        const forwarder = await this.getTrustedForwarder(paymaster);
        const nonce = await this.getNonce(forwarder, signer);
        const blockNumber = await signer.provider.getBlockNumber();

        let gasPrice = await this.signerService.getGasPrice();
        const minGasPricePlusTenPercent =
          (BigInt(getaddr.minGasPrice) * 110n) / 100n;
        if (gasPrice < minGasPricePlusTenPercent) {
          gasPrice = minGasPricePlusTenPercent;
        }

        return {
          from: signer.address, // Signer
          to: rebalancing.txHandler!, // ComptrollerProxy
          value: 0n,
          gas: gasEstimate,
          nonce: nonce,
          data: rebalancing.txData!,
          validUntil: BigInt(blockNumber) + 7200n, // same for all chains
          relayData: {
            gasPrice: gasPrice,
            pctRelayFee: config.pctRelayFee,
            baseRelayFee: 0n,
            relayWorker: getaddr.relayWorkerAddress,
            paymaster: paymaster,
            forwarder: forwarder,
            paymasterData:
              '0x0000000000000000000000000000000000000000000000000000000000000000',
            clientId: 1n,
          },
        };
      } catch (e) {
        retryCount++;
        this.sentryService.captureError(e as Error); // Capture the error on each attempt
        if (retryCount > retries) {
          throw Error(
            `Couldn't prepare relay request after ${retries} retries: ${e}`,
          );
        }

        const delay = initialDelay * Math.pow(2, retryCount - 1);
        await new Promise(resolve => setTimeout(resolve, delay)); // Wait before retrying
      }
    }

    // This should never be reached, as the try/catch should handle all cases.
    throw new Error('Unexpectedly reached end of retry loop.');
  }

  private async askForSignature(
    relayRequest: RelayRequestSignature,
    signer: JsonRpcSigner,
    config: ChainConfig,
  ): Promise<string> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.USER_SIGN,
    );

    try {
      return signer.signTypedData(
        {
          name: 'GSN Relayed Transaction',
          version: '2',
          chainId: config.chainIdAsDecimal,
          verifyingContract: relayRequest.relayData.forwarder,
        },
        signatureTypes,
        relayRequest,
      );
    } catch (e) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.USER_SIGN,
      );
      this.sentryService.captureError(e as Error);
      throw Error(`Relayer transaction not signed`);
    }
  }

  private async getSignedTransactionFromRelayer(
    relayRequest: RelayRequestSignature,
    signature: string,
    config: ChainConfig,
    getaddr: RelayerGetaddr,
  ): Promise<string> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.REQUEST_TX_FROM_RELAYER,
    );

    try {
      const getTxObservable = this.httpClient.post<RelayServerResponse>(
        `${config.relayerUrl}/gsn1/relay`,
        {
          metadata: {
            approvalData: '0x',
            relayHubAddress: getaddr.relayHubAddress,
            relayMaxNonce: 100000,
            signature: signature,
          },
          relayRequest: {
            request: {
              data: relayRequest.data,
              from: relayRequest.from,
              gas: relayRequest.gas,
              nonce: relayRequest.nonce,
              to: relayRequest.to,
              validUntil: relayRequest.validUntil,
              value: relayRequest.value,
            },
            relayData: {
              baseRelayFee: relayRequest.relayData.baseRelayFee,
              clientId: relayRequest.relayData.clientId,
              forwarder: relayRequest.relayData.forwarder,
              gasPrice: relayRequest.relayData.gasPrice,
              paymaster: relayRequest.relayData.paymaster,
              paymasterData: relayRequest.relayData.paymasterData,
              pctRelayFee: relayRequest.relayData.pctRelayFee,
              relayWorker: relayRequest.relayData.relayWorker,
            },
          },
        } as RelayServerRequest,
      );

      const response = await this.callWithRetry(getTxObservable);

      if (!response.signedTx) {
        this.transactionExecutionRepository.setFailedStep(
          TransactionExecutionStep.REQUEST_TX_FROM_RELAYER,
        );
        if (response.error) {
          this.sentryService.captureMsg(response.error);
          throw Error(response.error);
        } else {
          this.sentryService.captureMsg(`Couldn't get signedTx from relayer`);
          throw Error(`Couldn't get signedTx from relayer`);
        }
      }

      return response.signedTx;
    } catch (e) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.REQUEST_TX_FROM_RELAYER,
      );
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't get signed transaction from relayer`);
    }
  }

  private async broadcastTransaction(
    rebalancing: Rebalancing,
    signedTx: string,
    signer: JsonRpcSigner,
  ): Promise<void> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.BROADCAST,
    );

    try {
      const txResponse = await signer.provider.broadcastTransaction(signedTx);

      if (!txResponse) {
        this.transactionExecutionRepository.setFailedStep(
          TransactionExecutionStep.BROADCAST,
        );
        this.sentryService.captureMsg(`Couldn't broadcast transaction`);
        throw Error(`Couldn't broadcast transaction`);
      }

      this.alertService.showInfo('rebalancing.broadcasted', {
        txLink: txResponse.hash
          ? BLOCK_EXPLORER_TX_LINK(
              txResponse.hash,
              this.chainRepository.store.getValue().chain,
            )
          : undefined,
      });

      this.rebalancingRepository.setTxHash(txResponse.hash);

      const transaction = new Transaction(
        txResponse,
        'Batch Trade',
        TransactionType.BATCH_TRADE,
      );

      try {
        this.transactionExecutionRepository.setTransaction(transaction);
        await this.transactionService.addTransaction(
          transaction,
          rebalancing.id,
        );
      } catch (e) {
        // empty on purpose
      }

      transaction.getFinishedObservable().subscribe((finished: boolean) => {
        if (finished) {
          if (transaction.success) {
            this.rebalancingRepository.setStep(RebalancingStep.FINISHED);
            rebalancing.trades.forEach(trade => {
              this.rebalancingRepository.updateTrade(trade.id, {
                tradeState: TradeSateEnum.SUCCESS,
              });
            });
            this.transactionExecutionRepository.setStep(
              TransactionExecutionStep.SUCCESS,
            );
            this.alertService.showSuccess(
              'rebalancing.success',
              {
                txLink: transaction.hash
                  ? BLOCK_EXPLORER_TX_LINK(
                      transaction.hash,
                      this.chainRepository.store.getValue().chain,
                    )
                  : undefined,
              },
              false,
              -1,
            );
          } else {
            this.transactionExecutionRepository.setStep(
              TransactionExecutionStep.ERROR,
            );
            this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
            rebalancing.trades.forEach(trade => {
              this.rebalancingRepository.updateTrade(trade.id, {
                tradeState: TradeSateEnum.READY,
              });
            });
            this.alertService.showError(
              'rebalancing.error.unexpectedTxError',
              {
                txLink: transaction.hash
                  ? BLOCK_EXPLORER_TX_LINK(
                      transaction.hash,
                      this.chainRepository.store.getValue().chain,
                    )
                  : undefined,
              },
              false,
              -1,
            );
          }
        }
      });
    } catch (e) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.BROADCAST,
      );
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't broadcast transaction`);
    }
  }

  private getConfig(): ChainConfig {
    if (!this.chainRepository.store.getValue().chain) {
      throw Error(`Couldn't load relayer config`);
    }

    return this.chainConfigs[
      this.chainRepository.store.getValue().chain!.identifier
    ];
  }

  private async getAddr(config: ChainConfig): Promise<RelayerGetaddr> {
    try {
      const getAddrObservable = this.httpClient.get<RelayerGetaddr>(
        `${config.relayerUrl}/gsn1/getaddr`,
      );

      return await this.callWithRetry(getAddrObservable);
    } catch (e) {
      throw Error(`Couldn't load relayer getaddr`);
    }
  }

  private callWithRetry<T>(
    getAddrObservable: Observable<T>,
    retries = 2,
    initialDelay = 100,
  ): Promise<T> {
    return lastValueFrom(
      getAddrObservable.pipe(
        retry({
          count: retries,
          delay: (error, retryCount) => {
            const delay = initialDelay * Math.pow(2, retryCount - 1); // Exponential backoff
            this.sentryService.captureError(error as Error); // Capture error on each retry attempt
            return timer(delay);
          },
        }),
        catchError(err => {
          //This catch block is for handling the final error after all retries are exhausted.
          return throwError(() => err);
        }),
      ),
    );
  }

  public async isRelayerEnabled(vault: Address): Promise<boolean> {
    try {
      const vaultContract = IVault__factory.connect(
        vault,
        this.signerService.getSigner(),
      );
      const comptrollerProxy = await vaultContract.getAccessor();

      return !AddressUtil.equalsZero(
        await this.getGasRelayPaymaster(comptrollerProxy),
      );
    } catch (e) {
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't check if relayer is enabled`);
    }
  }

  private async getGasRelayPaymaster(
    comptrollerProxy: Address,
  ): Promise<Address> {
    try {
      const comptroller = await this.getComptrollerContract(comptrollerProxy);
      return comptroller.getGasRelayPaymaster();
    } catch (e) {
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't get paymaster for ${comptrollerProxy}`);
    }
  }

  private async getComptrollerContract(
    address: Address,
  ): Promise<IComptrollerLib> {
    return IComptrollerLib__factory.connect(
      address,
      this.signerService.getSigner(),
    );
  }

  private async getRelayerBalance(gasRelayPaymaster: Address): Promise<bigint> {
    try {
      const paymaster =
        await this.getGasRelayPaymasterContract(gasRelayPaymaster);
      return paymaster.getRelayHubDeposit();
    } catch (e) {
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't get relayer balance for ${gasRelayPaymaster}`);
    }
  }

  private async getTrustedForwarder(
    gasRelayPaymaster: Address,
  ): Promise<Address> {
    try {
      const paymaster =
        await this.getGasRelayPaymasterContract(gasRelayPaymaster);
      return paymaster.trustedForwarder();
    } catch (e) {
      this.sentryService.captureError(e as Error);
      throw Error(`Couldn't get forwarder for ${gasRelayPaymaster}`);
    }
  }

  private async getGasRelayPaymasterContract(
    address: Address,
  ): Promise<IGasRelayPaymasterLib> {
    return IGasRelayPaymasterLib__factory.connect(
      address,
      this.signerService.getSigner(),
    );
  }

  private async getNonce(
    trustedForwarder: Address,
    signer: JsonRpcSigner,
  ): Promise<bigint> {
    try {
      const abi = ['function getNonce(address sender) view returns (uint256)'];
      const iface = new Interface(abi);

      const forwarderContract = new Contract(trustedForwarder, iface, signer);
      return forwarderContract['getNonce'](signer.address);
    } catch (e) {
      this.sentryService.captureError(e as Error);
      throw Error(
        `Couldn't get nonce for ${signer.address} on ${trustedForwarder}`,
      );
    }
  }

  // COPY FROM batch-trade.service START
  private async estimateGasUnits(rebalancing: Rebalancing): Promise<bigint> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.SIMULATE,
    );

    const signer = this.signerService.getSigner();
    if (!signer) {
      throw new Error('Wallet not connected anymore');
    }

    try {
      const estimate = await signer.estimateGas({
        to: rebalancing.txHandler,
        data: rebalancing.txData,
        value: rebalancing.txValue,
      });

      return Promise.resolve((estimate * 120n) / 100n); // 20% Buffer
    } catch (e: any) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.SIMULATE,
      );
      this.sentryService.captureError(e as Error);
      this.handleEstimateGasError(e, rebalancing);
      throw e;
    }
  }

  private handleEstimateGasError(e: any, rebalancing: Rebalancing): void {
    this.alertService.showError(
      'rebalancing.error.estimationError',
      undefined,
      false,
      -1,
    );

    let decodable = false;
    try {
      let revertData = e.error?.data?.data?.data;
      if (!revertData) {
        revertData = e.error?.data?.originalError?.data;
      }

      if (revertData) {
        const decodedError =
          BatchTrade__factory.createInterface().parseError(revertData);

        this.errorHandler.handleError(
          `Couldn't execute rebalancing with id ${rebalancing.id} due to: ${decodedError}`,
        );
        this.sentryService.captureMsg(
          `Couldn't execute rebalancing with id ${rebalancing.id} due to: ${decodedError}`,
        );
        if (!this.appConfig.production) {
          console.error(decodedError);
        }

        if (decodedError?.args['index']) {
          const tradeIndex = +decodedError.args['index'].toString();
          this.showAlertForFailingTrade(rebalancing.trades[tradeIndex]);
        }
        decodable = true;
      } else {
        console.error(`Couldn't extract revert data from error`, e);
      }
    } catch (inner) {
      console.error(`Couldn't decode gas estimation error: `, inner);
    }

    // TODO: this doesn't work if the signer doesn't hold the assets (e.g. enzyme)
    if (!decodable) {
      // this.tryToFindFailingTradeBySingleGasEstimations(rebalancing);
    }
  }

  private showAlertForFailingTrade(trade: Trade): void {
    this.alertService.showError(
      'rebalancing.error.tradeFails',
      {
        tradePairString: trade.toString(),
      },
      false,
      -1,
    );
  }
  // COPY FROM batch-trade.service END
}
